import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import styled from "styled-components"

import { ReactComponent as Icon } from "assets/svg/icon-select.svg"

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 16px;
  width: 100%;
  min-width: 0;
`

const ArrowIcon = styled(Icon)<{ type: "left" | "right" }>`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  transform: rotate(${({ type }) => (type === "left" ? 90 : -90)}deg);
  cursor: pointer;
  margin-right: ${({ type }) => (type === "left" ? "8px" : "0")};
  margin-left: ${({ type }) => (type === "right" ? "8px" : "0")};
  margin-bottom: 12px;

  path {
    fill: #94a3b8;
  }

  &:hover {
    path {
      fill: #334155;
    }
  }
`

const TabsContainer = styled.div`
  display: flex;
  width: 100%;
  position: relative;
  min-width: 0;
  overflow-x: auto;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;

  &::after {
    content: " ";
    position: absolute;
    bottom: 0px;
    height: 1px;
    width: 100%;
    border-bottom: 1px solid #cbd5e1;
    z-index: 0;
    transition: all 0.2s;
  }
`

const Tab = styled.button<{ active: boolean }>`
  color: ${({ active }) => (active ? "#2db2c8" : "#64748b")};
  font-size: 16px;
  font-weight: 500;
  padding: 0 15px 12px 15px;
  margin-bottom: -1px;
  border: none;
  border-bottom: 2px solid ${({ active }) => (active ? "#2db2c8" : "#cbd5e1")};
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;
  opacity: ${({ active }) => (active ? 1 : 0.7)};
  flex-shrink: 0;
  white-space: nowrap;
  position: relative;
  z-index: 1;

  &:hover {
    opacity: 1;
    color: ${({ active }) => (active ? "#2db2c8" : "#334155")};
  }
`

export interface TabItem {
  label: string
  path: string
}

interface TabsProps {
  tabs: TabItem[]
  basePath: string
  defaultTab?: string
}

export const Tabs = ({ tabs, basePath, defaultTab }: TabsProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [showScroll, setShowScroll] = useState(false)
  const container = useRef<HTMLDivElement>(null)

  const getActiveTab = () => {
    const path = location.pathname.split("/").pop() || ""
    if (path === basePath.split("/").pop() || path === "") {
      return defaultTab || tabs[0]?.path || ""
    }
    return path
  }

  const activeTab = getActiveTab()

  useEffect(() => {
    const wrapper = container.current

    if (wrapper === null) {
      return
    }

    const checkScroll = () => {
      if (wrapper.scrollWidth > wrapper.clientWidth) {
        setShowScroll(true)
      } else {
        setShowScroll(false)
      }
    }

    checkScroll()

    const handleResize = () => {
      checkScroll()
    }

    window.addEventListener("resize", handleResize)

    const timeoutId = setTimeout(checkScroll, 0)

    return () => {
      window.removeEventListener("resize", handleResize)
      clearTimeout(timeoutId)
    }
  }, [tabs, activeTab])

  const handleTabClick = (path: string) => {
    navigate(`${basePath}/${path}`)

    setTimeout(() => {
      const activeTabElement = container.current?.querySelector(`[data-tab-path="${path}"]`)
      if (activeTabElement) {
        activeTabElement.scrollIntoView({ block: "nearest", behavior: "smooth" })
      }
    }, 0)
  }

  const handleScroll = (direction: "left" | "right") => {
    const wrapper = container.current
    const tabs = (container.current ? [...container.current.children] : []) as HTMLButtonElement[]
    const sortedTabs = direction === "left" ? tabs.reverse() : tabs

    if (tabs.length === 0 || wrapper === null) {
      return
    }

    for (let i = 0; i < sortedTabs.length; i++) {
      const elementRect = sortedTabs[i].getBoundingClientRect()
      const containerRect = wrapper.getBoundingClientRect()

      if (direction === "right") {
        if (
          (elementRect.right > containerRect.right + 1 && elementRect.left > containerRect.right) ||
          i === sortedTabs.length - 1
        ) {
          sortedTabs[i].scrollIntoView({ block: "nearest", behavior: "smooth" })
          break
        }
      } else {
        if (
          (elementRect.left < containerRect.left - 1 && elementRect.right < containerRect.left) ||
          i === sortedTabs.length - 1
        ) {
          sortedTabs[i].scrollIntoView({ block: "nearest", behavior: "smooth" })
          break
        }
      }
    }
  }

  return (
    <Wrapper>
      {showScroll && <ArrowIcon type="left" onClick={() => handleScroll("left")} />}
      <TabsContainer ref={container}>
        {tabs.map(tab => (
          <Tab
            key={tab.path}
            data-tab-path={tab.path}
            active={activeTab === tab.path}
            onClick={() => handleTabClick(tab.path)}
          >
            {tab.label}
          </Tab>
        ))}
      </TabsContainer>
      {showScroll && <ArrowIcon type="right" onClick={() => handleScroll("right")} />}
    </Wrapper>
  )
}
